
import { v4 as uuidv4 } from "uuid";
import { CreatureLimitation, Spell, SpellLimitation, UpgradableNumber } from "@dndModels";
import { RARITY_NUM_TO_STR_MAP, REFRESH_RULES } from "@dndModels/Constants";
import { RecipeComponentType, ItemType, RefreshRuleType, WorldSetting, DataType } from "@dndModels/Types";
import { DandDFeatureSource } from "@dndModels/feature_sources";

export abstract class Item extends DandDFeatureSource {
  data_type: DataType = "item";
  feature_source_type = "Item";
  item_type: ItemType = "Other";
  b_weight = 0; // in lbs
  cost = 0;
  // magical: boolean = false;
  environments: string[] = ["Any"];
  // settings: string[] = ["Any"];

  // crafting: CraftingData = new CraftingData();

  // rarity: number = 0; // 0-Common, 1-Uncommon, 2-Rare, 3-Very-Rare, 4-Legendary
  min_rarity = 0;
  max_rarity = 0;
  name_formula = "";
  rarity_names: { [rarity: number]: string } = {};

  get rarities() {
    const rarities: string[] = [];
    for (let i = this.min_rarity; i <= this.max_rarity; ++i) {
      const rarity = RARITY_NUM_TO_STR_MAP[i];
      if (rarity) {
        rarities.push(rarity);
      }
    }
    return rarities;
  }

  get rarities_string() {
    return this.rarities.join(", ");
  }

  recipes: Recipe[] = [];
  
  attunement = false;

  charges = 0;
  charge_refresh_rule: RefreshRuleType = "None";
  refresh_dice_count = 0;
  refresh_dice_size = 0;
  refresh_dice_flat = 1;
  // If this is true then it means the objects starts with 0, 
  // and refreshing reverts it back to 0.
  // It's for items like the Rod of Absorption, 
  // which are capable of storing charges, 
  // but need to be charged up by using an ability.
  // Then those charges can then be used for other abilities.
  invert_charges = false; 
  
  ignore_proficiency_requirements = false;
  spell_dc: UpgradableNumber = new UpgradableNumber();
  spell_attack: UpgradableNumber = new UpgradableNumber();
  spell_mod: UpgradableNumber = new UpgradableNumber();
  
  // Extra Base Type is for creating things that draw on something else to 
  // define the specifics of it.  It works similarly to Selectable, 
  // but instead of the user getting to select as part of the feature, it 
  // just creates different items for each option.
  // For Example, spell scrolls would have an extra_base_type === "Spell"
  // Potions of Resistance would have an extra_base_type === "Damage Type"
  extra_base_type = "None";
  // We probably want to limit these, 
  // if for no other reason than to give different rarities
  // For Spell this is Levels, on Spell Lists, and Schools.
  // For the others it's a list of options.
  extra_base_limits: any = null; 

  possible_mik_ids: string[] = [];
  weapon_keyword_ids: string[] = [];

  // Spell Storing Items
  spell_storing = false;
  spell_limit: SpellLimitation = new SpellLimitation();
  count_rules: SpellCountRules = new SpellCountRules();

  parse_errors: string[] = [];

  constructor(obj?: any) {
    super(obj, "Item");
    if (obj) {
      this.item_type = obj.item_type ? obj.item_type : "Other";
      this.b_weight = obj.weight ? +obj.weight : 0;
      this.cost = obj.cost ? +obj.cost : 0;
      this.environments = obj.environments ? [...obj.environments] : ["Any"];
      // this.settings = obj.settings ? [...obj.settings] : ["Any"];
      if (obj.settings && obj.settings.length > 0 && this.world_settings.length === 0) {
        this.world_settings = obj.settings as WorldSetting[];
      }
      if (this.world_settings.includes("Any")) {
        this.world_settings = this.world_settings.filter(o => o !== "Any");
      }
      
      this.min_rarity = obj.min_rarity !== undefined ? obj.min_rarity : (obj.rarity ? obj.rarity : 0);
      this.max_rarity = obj.max_rarity !== undefined ? obj.max_rarity : this.min_rarity;
      this.rarity_names = obj.rarity_names ? obj.rarity_names : {};
      // if (this.name.includes("Rod of Absorp")) {
      //   console.log(obj);
      //   debugger;
      // }
      this.recipes = obj.recipes ? obj.recipes.map((o: any) => new Recipe(o)) : (obj.recipe && obj.recipe.components && obj.recipe.components.length > 0 ? [new Recipe(obj.recipe)] : []);
      for (const recipe of this.recipes) {
        recipe.item_id = this._id;
      }

      this.name_formula = obj.name_formula ? obj.name_formula : "";

      this.attunement = obj.attunement ? obj.attunement : false;

      this.charges = obj.charges ? obj.charges : 0;
      this.charge_refresh_rule = obj.charge_refresh_rule ? obj.charge_refresh_rule : "None";
      if (!REFRESH_RULES.includes(this.charge_refresh_rule)) {
        this.charge_refresh_rule = "None";
      }
      this.refresh_dice_count = obj.refresh_dice_count ? obj.refresh_dice_count : 0;
      this.refresh_dice_size = obj.refresh_dice_size ? obj.refresh_dice_size : 0;
      this.refresh_dice_flat = obj.refresh_dice_flat ? obj.refresh_dice_flat : 0;
      this.invert_charges = obj.invert_charges ? obj.invert_charges : false;

      this.ignore_proficiency_requirements = obj.ignore_proficiency_requirements ? obj.ignore_proficiency_requirements : false;
      this.spell_dc = obj.spell_dc ? new UpgradableNumber(obj.spell_dc) : new UpgradableNumber();
      this.spell_attack = obj.spell_attack ? new UpgradableNumber(obj.spell_attack) : new UpgradableNumber();
      this.spell_mod = obj.spell_mod ? new UpgradableNumber(obj.spell_mod) : new UpgradableNumber();
      this.extra_base_type = obj.extra_base_type ? obj.extra_base_type : "None";
      this.extra_base_limits = obj.extra_base_limits ? obj.extra_base_limits : {};
      if (this.extra_base_limits.length && this.extra_base_limits.length === 1 && this.extra_base_limits[0] === "Any") {
        this.extra_base_limits = [];
      }
      if (this.name.includes(" Of ")) {
        this.name = this.name.replace(" Of ", " of ");
      }
      this.spell_storing = obj.spell_storing ? obj.spell_storing : false;
      this.spell_limit = obj.spell_limit ? new SpellLimitation(obj.spell_limit) : new SpellLimitation();
      this.count_rules = obj.count_rules ? new SpellCountRules(obj.count_rules) : new SpellCountRules();
    }
  }

  // load(obj: any): void {
  //   super.load(obj);
  //   if (obj) {
  //     this.item_type = obj.item_type ? obj.item_type : "Other";
  //     this.b_weight = obj.weight ? +obj.weight : 0;
  //     this.cost = obj.cost ? +obj.cost : 0;
  //     this.environments = obj.environments ? [...obj.environments] : ["Any"];
  //     // this.settings = obj.settings ? [...obj.settings] : ["Any"];
  //     if (obj.settings && obj.settings.length > 0 && this.world_settings.length === 0) {
  //       this.world_settings = obj.settings as WorldSetting[];
  //     }
  //     if (this.world_settings.includes("Any")) {
  //       this.world_settings = this.world_settings.filter(o => o !== "Any");
  //     }
      
  //     this.min_rarity = obj.min_rarity !== undefined ? obj.min_rarity : (obj.rarity ? obj.rarity : 0);
  //     this.max_rarity = obj.max_rarity !== undefined ? obj.max_rarity : this.min_rarity;
  //     this.rarity_names = obj.rarity_names ? obj.rarity_names : {};
  //     if (this.name.includes("Rod of Absorp")) {
  //       console.log(obj);
  //     }
  //     this.recipes = obj.recipes ? obj.recipes.map((o: any) => new Recipe(o)) : (obj.recipe && obj.recipe.components && obj.recipe.components.length > 0 ? [new Recipe(obj.recipe)] : []);
  //     for (const recipe of this.recipes) {
  //       recipe.item_id = this._id;
  //     }

  //     this.name_formula = obj.name_formula ? obj.name_formula : "";

  //     this.attunement = obj.attunement ? obj.attunement : false;

  //     this.charges = obj.charges ? obj.charges : 0;
  //     this.charge_refresh_rule = obj.charge_refresh_rule ? obj.charge_refresh_rule : "None";
  //     if (!REFRESH_RULES.includes(this.charge_refresh_rule)) {
  //       this.charge_refresh_rule = "None";
  //     }
  //     this.refresh_dice_count = obj.refresh_dice_count ? obj.refresh_dice_count : 0;
  //     this.refresh_dice_size = obj.refresh_dice_size ? obj.refresh_dice_size : 0;
  //     this.refresh_dice_flat = obj.refresh_dice_flat ? obj.refresh_dice_flat : 0;
  //     this.invert_charges = obj.invert_charges ? obj.invert_charges : false;

  //     this.ignore_proficiency_requirements = obj.ignore_proficiency_requirements ? obj.ignore_proficiency_requirements : false;
  //     this.spell_dc = obj.spell_dc ? obj.spell_dc : 0;
  //     this.spell_attack = obj.spell_attack ? obj.spell_attack : 0;
  //     this.extra_base_type = obj.extra_base_type ? obj.extra_base_type : "None";
  //     this.extra_base_limits = obj.extra_base_limits ? obj.extra_base_limits : {};
  //     if (this.extra_base_limits.length && this.extra_base_limits.length === 1 && this.extra_base_limits[0] === "Any") {
  //       this.extra_base_limits = [];
  //     }
  //     if (this.name.includes(" Of ")) {
  //       this.name = this.name.replace(" Of ", " of ");
  //     }
  //     this.spell_storing = obj.spell_storing ? obj.spell_storing : false;
  //     this.spell_limit = obj.spell_limit ? new SpellLimitation(obj.spell_limit) : new SpellLimitation();
  //     this.count_rules = obj.count_rules ? new SpellCountRules(obj.count_rules) : new SpellCountRules();
  //   }
  // }

  get_detail_format() {
    const str = `${super.get_detail_format()}; ${this.rarity_string} - ${this.item_type}`;
    return str.trim();
  }

  get magical() {
    return this.min_rarity > 0;
  }

  toDBObj(): any {
    const base_db_obj = super.toDBObj();
    return {
      ...base_db_obj,
      item_type: this.item_type,
      weight: this.b_weight,
      cost: this.cost,
      environments: [...this.environments],
      // settings: [...this.settings],
      min_rarity: this.min_rarity,
      max_rarity: this.max_rarity,
      name_formula: this.name_formula,
      rarity_names: this.rarity_names,
      recipes: this.recipes.map(o => o.toDBObj()),
      attunement: this.attunement,
      charges: this.charges,
      charge_refresh_rule: this.charge_refresh_rule,
      refresh_dice_count: this.refresh_dice_count,
      refresh_dice_size: this.refresh_dice_size,
      refresh_dice_flat: this.refresh_dice_flat,
      invert_charges: this.invert_charges,
      ignore_proficiency_requirements: this.ignore_proficiency_requirements,
      spell_dc: this.spell_dc.toDBObj(),
      spell_attack: this.spell_attack.toDBObj(),
      spell_mod: this.spell_mod.toDBObj(),
      extra_base_type: this.extra_base_type,
      extra_base_limits: this.extra_base_limits,
      spell_storing: this.spell_storing,
      spell_limit: this.spell_limit.toDBObj(),
      count_rules: this.count_rules.toDBObj(),
    };
  }

  toLight(): any {
    const base_db_obj = super.toLight();
    return {
      ...base_db_obj,
      item_type: this.item_type,
      weight: this.b_weight,
      cost: this.cost,
      environments: [...this.environments],
      // settings: [...this.settings],
      min_rarity: this.min_rarity,
      max_rarity: this.max_rarity,
      name_formula: this.name_formula,
      rarity_names: this.rarity_names,
      recipes: this.recipes.map(o => o.toDBObj()),
      attunement: this.attunement,
      charges: this.charges,
      charge_refresh_rule: this.charge_refresh_rule,
      refresh_dice_count: this.refresh_dice_count,
      refresh_dice_size: this.refresh_dice_size,
      refresh_dice_flat: this.refresh_dice_flat,
      invert_charges: this.invert_charges,
      ignore_proficiency_requirements: this.ignore_proficiency_requirements,
      spell_dc: this.spell_dc.toDBObj(),
      spell_attack: this.spell_attack.toDBObj(),
      spell_mod: this.spell_mod.toDBObj(),
      extra_base_type: this.extra_base_type,
      extra_base_limits: this.extra_base_limits,
      spell_storing: this.spell_storing,
      spell_limit: this.spell_limit.toDBObj(),
      count_rules: this.count_rules.toDBObj(),
    };
  }

  useNameForRarity({ selected_rarity = -1, extra = "", extra_spell = null }: { selected_rarity?: number, extra?: string, extra_spell?: Spell | null }) {
    const name_rarities = Object.keys(this.rarity_names);
    let name = "";
    if (selected_rarity === -1 || (this.name_formula === "" && name_rarities.length === 0)) {
      if (extra !== "" && this.name_formula !== "") {
        name = this.name_formula;
      } else {
        name = this.name;
      }
    } else if (name_rarities.length === 0) {
      if (this.name_formula !== "") {
        name = this.name_formula;
        name = name.replace("{selected_rarity}", `${selected_rarity}`);
      } else {
        name = this.name;
      }
    } else if (name_rarities.includes(`${selected_rarity}`)) {
      name = this.rarity_names[selected_rarity];
    } else {
      if (extra !== "" && this.name_formula !== "") {
        name = this.name_formula;
      } else {
        name = this.name;
      }
    }
    if (extra !== "") {
      if (extra_spell) {
        name = name.replace("{extra.name}", extra_spell.name);
      } else {
        name = name.replace("{extra}", extra);
      }
    }
    return name;
  }

  get weight(): number {
    return this.b_weight;
  }

  set weight(value: number) {
    this.b_weight = value;
  }

  get rarity_string(): string {
    if (this.min_rarity === this.max_rarity) {
      return RARITY_NUM_TO_STR_MAP[this.min_rarity];
    } else {
      return `${RARITY_NUM_TO_STR_MAP[this.min_rarity]} - ${RARITY_NUM_TO_STR_MAP[this.max_rarity]}`;
    }
  }

  // get color(): string {
  //   let color = "inherit"; // Common
  //   switch (this.min_rarity) {
  //     case 1: color = "green"; break; // Uncommon
  //     case 2: color = "blue"; break; // Rare
  //     case 3: color = "purple"; break; // Very-Rare
  //     case 4: color = "orange"; break; // Legendary
  //     case 5: color = "brown"; break; // Artifact
  //   }
  //   return color;
  // }

  copy(copyMe: Item, drop_ids = false): void {
    this.copyItem(copyMe, drop_ids);
  }

  clone(drop_ids = false) {
    const new_obj = super.clone(drop_ids) as Item;
    return new_obj;
  }

  copyItem(copyMe: Item, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    this.item_type = copyMe.item_type;
    this.b_weight = copyMe.b_weight;
    this.cost = copyMe.cost;
    // this.magical = copyMe.magical;
    this.min_rarity = copyMe.min_rarity;
    this.max_rarity = copyMe.max_rarity;
    this.name_formula = copyMe.name_formula;
    this.rarity_names = {...copyMe.rarity_names};
    this.recipes = copyMe.recipes.map(o => o.clone(drop_ids));
    this.attunement = copyMe.attunement;
    this.charges = copyMe.charges;
    this.charge_refresh_rule = copyMe.charge_refresh_rule;
    this.refresh_dice_count = copyMe.refresh_dice_count;
    this.refresh_dice_size = copyMe.refresh_dice_size;
    this.refresh_dice_flat = copyMe.refresh_dice_flat;
    this.invert_charges = copyMe.invert_charges;
    this.ignore_proficiency_requirements = copyMe.ignore_proficiency_requirements;
    this.spell_dc = copyMe.spell_dc.clone();
    this.spell_attack = copyMe.spell_attack.clone();
    this.spell_mod = copyMe.spell_mod.clone();
    this.extra_base_type = copyMe.extra_base_type;
    if (this.extra_base_type === "Spell") {
      const extra_base_limits = copyMe.extra_base_limits as { levels: string[], schools: string[], onSpellList: string[] };
      this.extra_base_limits = {
        levels: [...extra_base_limits.levels],
        schools: [...extra_base_limits.schools],
        onSpellList: [...extra_base_limits.onSpellList]
      };
    } else if (this.extra_base_type !== "None") {
      const extra_base_limits = copyMe.extra_base_limits as string[];
      this.extra_base_limits = [...extra_base_limits];
    } else {
      this.extra_base_limits = null;
    }
    this.spell_storing = copyMe.spell_storing;
    this.spell_limit = copyMe.spell_limit.clone();
    this.count_rules = copyMe.count_rules.clone();
  }
}

export class DeckItem extends Item {
  deck_id = ""; // If this is not empty then it's the id of the GameDeck it goes with.
  // If deck_id is empty then we put the cards here.
  cards: { true_id: string, name: string, description: string, reshuffle: boolean }[] = [];

  constructor(obj?: any) {
    super(obj);
    this.item_type = "Deck";
    if (obj) {
      this.deck_id = obj.deck_id ? obj.deck_id : "";
      this.cards = obj.cards ? obj.cards : [];
      for (const card of this.cards.filter(o => o.reshuffle === undefined)) {
        card.reshuffle = true;
      }
    }
  }

  // load(obj: any): void {
  //   super.load(obj);
  //   if (obj) {
  //     this.deck_id = obj.deck_id ? obj.deck_id : "";
  //     this.cards = obj.cards ? obj.cards : [];
  //     for (const card of this.cards.filter(o => o.reshuffle === undefined)) {
  //       card.reshuffle = true;
  //     }
  //   }
  // }

  toDBObj(): any {
    const base_db_obj = super.toDBObj();
    return {
      ...base_db_obj,
      deck_id: this.deck_id,
      cards: this.cards
    };
  }

  toLight(): any {
    const base_db_obj = super.toLight();
    return {
      ...base_db_obj,
      deck_id: this.deck_id,
      cards: this.cards
    };
  }

  clone(drop_ids = false): DeckItem {
    const new_obj = new DeckItem();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }

  copy(copyMe: DeckItem, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    this.deck_id = copyMe.deck_id;
    this.cards = copyMe.cards.map(o => {
      return { true_id: o.true_id, name: o.name, description: o.description, reshuffle: o.reshuffle !== undefined ? o.reshuffle : true };
    });
  }
}

/**
 * Gems can come in various sizes and values, and most spell components that specify a value are gems.
 * CharacterGem will have easy controls for changing the weight and value.
 * 
 * There's also the option of specifying sizes with values on the base item to make it even easier.
 */
export class GemItem extends Item {
  sizes: { 
    true_id: string,
    name: string, 
    cost: number, 
    weight: number, 
    ac: number,
    hp: number,
    extraction: string
  }[] = [];

  constructor(obj?: any) {
    super(obj);
    this.item_type = "Gem";
    if (obj) {
      this.sizes = obj.sizes ? obj.sizes : [];
    }
  }

  // load(obj: any): void {
  //   super.load(obj);
  //   if (obj) {
  //     this.sizes = obj.sizes ? obj.sizes : [];
  //   }
  // }

  toDBObj(): any {
    const base_db_obj = super.toDBObj();
    return {
      ...base_db_obj,
      sizes: this.sizes,
    };
  }

  toLight(): any {
    const base_db_obj = super.toLight();
    return {
      ...base_db_obj,
      sizes: this.sizes,
    };
  }

  clone(drop_ids = false): GemItem {
    const new_obj = new GemItem();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }

  copy(copyMe: GemItem, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    this.sizes = copyMe.sizes.map(o => { return {...o}; });
  }
}

export class Recipe {
  true_id = "";
  item_id = "";
  name = "";
  cr = 0; // Minimum Rounded Average CR of the creatures that their parts are going into the recipe.  Should never be below 1.
  components: RecipeComponent[] = [];
  extra_details = "";

  constructor(obj?: any) {
    this.true_id = obj && obj.true_id ? obj.true_id : uuidv4().toString();
    if (obj) {
      this.item_id = obj.item_id ? obj.item_id : "";
      this.name = obj.name && obj.name !== "" ? obj.name : "Default";
      this.cr = obj.cr ? obj.cr : 0;
      this.components = obj.components ? obj.components.map((o: any) => new RecipeComponent(o)) : [];
      this.extra_details = obj.extra_details ? obj.extra_details : "";
    }
  }

  toDBObj(): any {
    return {
      true_id: this.true_id,
      item_id: this.item_id,
      name: this.name,
      cr: this.cr,
      components: this.components.map(o => o.toDBObj()),
      extra_details: this.extra_details
    };
  }

  toString() {
    const recipeString = this.name + "\n" + this.components.map(o => o.toString()).join("\n") + "\n" + this.extra_details;

    return recipeString;
  }

  clone(drop_ids = false): Recipe {
    const new_obj = new Recipe();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }

  copy(copyMe: Recipe, drop_ids = false): void {
    if (!drop_ids) {
      this.true_id = copyMe.true_id;
    }
    this.item_id = copyMe.item_id;
    this.name = copyMe.name;
    this.cr = copyMe.cr;
    this.components = copyMe.components.map(o => o.clone(drop_ids));
    this.extra_details = copyMe.extra_details;
  }

  merge(copyMe: Recipe): void {
    this.cr += copyMe.cr;
    for (const o of copyMe.components) {
      const clone = o.clone(true);
      clone.source_recipe_id = copyMe.true_id;
      this.components.push(clone);
    }
    if (this.extra_details.length > 0 && copyMe.extra_details.length > 0) {
      this.extra_details += "\n" + copyMe.extra_details;
    } else if (copyMe.extra_details.length > 0) {
      this.extra_details = copyMe.extra_details;
    }
    if (this.name === "") {
      this.name = copyMe.name;
    }
  }
}

export class RecipeComponent {
  true_id = "";
  component_type: RecipeComponentType = "Any";
  creature_limit: CreatureLimitation = new CreatureLimitation();
  description = "";
  source_recipe_id = "";

  constructor(obj?: any) {
    this.true_id = obj && obj.true_id ? obj.true_id : uuidv4().toString();
    if (obj) {
      this.component_type = obj.component_type ? obj.component_type : "Any";
      this.creature_limit = obj.creature_limit ? new CreatureLimitation(obj.creature_limit) : new CreatureLimitation();
      this.description = obj.description ? obj.description : "";
      this.source_recipe_id = obj.source_recipe_id ? obj.source_recipe_id : "";
    }
  }

  toDBObj(): any {
    return {
      true_id: this.true_id,
      component_type: this.component_type,
      creature_limit: this.creature_limit.toDBObj(),
      description: this.description,
      source_recipe_id: this.source_recipe_id
    };
  }

  toString(): string {
    let str = this.description;
    if (str.length === 0) {
      str = this.component_type;
      if (!this.creature_limit.isEmpty()) {
        str += `: ${this.creature_limit.toString()}`;
      }
    }
    return str;
  }

  clone(drop_ids = false): RecipeComponent {
    const new_obj = new RecipeComponent();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }

  copy(copyMe: RecipeComponent, drop_ids = false): void {
    if (!drop_ids) {
      this.true_id = copyMe.true_id;
    }
    this.component_type = copyMe.component_type;
    this.creature_limit = copyMe.creature_limit.clone();
    this.description = copyMe.description;
    this.source_recipe_id = copyMe.source_recipe_id;
  }
}

// export class Prosthesis extends Item {
//   prosthesis_property_ids: string[] = [];
//   body_locations: string[] = [];

//   constructor(obj?: any) {
//     super(obj);
//     this.item_type = "Deck";
//     if (obj) {
//       this.prosthesis_property_ids = obj.prosthesis_property_ids ? obj.prosthesis_property_ids : [];
//       this.body_locations = obj.body_locations ? obj.body_locations : [];
//     }
//   }

//   toDBObj(): any {
//     const base_db_obj = super.toDBObj();
//     return {
//       ...base_db_obj,
//       prosthesis_property_ids: this.prosthesis_property_ids,
//       body_locations: this.body_locations
//     };
//   }

//   clone(drop_ids: boolean = false): Prosthesis {
//     const new_obj = new Prosthesis();
//     new_obj.copy(this, drop_ids);
//     return new_obj;
//   }

//   copy(copyMe: Prosthesis, drop_ids: boolean = false): void {
//     super.copy(copyMe, drop_ids);
//     this.prosthesis_property_ids = [...copyMe.prosthesis_property_ids];
//     this.body_locations = [...copyMe.body_locations];
//   }
// }

// // currently based on how it's done in Ryoko's Guide beta v0.06
// // Unfortunately, the parts about magic items and enchanting 
// // are currently just saying use the stuff in Heliana's, which I don't have.
// // I'll wait for that before implementing.
// export class CraftingData {
//   material_cost: number = 0; // in gp
//   tool_id: string = "";
//   dc: number = 0;
//   hours: number = 0;
  
//   constructor(obj?: any) {
//     if (obj) {
//       // this.deck_id = obj.deck_id ? obj.deck_id : "";
//       // this.cards = obj.cards ? obj.cards : [];
//       // for (const card of this.cards.filter(o => o.reshuffle === undefined)) {
//       //   card.reshuffle = true;
//       // }
//     }
//   }

//   toDBObj(): any {
//     return {
//     };
//   }

//   clone(): CraftingData {
//     const new_obj = new CraftingData();
//     new_obj.copy(this);
//     return new_obj;
//   }

//   copy(copyMe: CraftingData): void {
//     // this.deck_id = copyMe.deck_id;
//     // this.cards = copyMe.cards.map(o => {
//     //   return { true_id: o.true_id, name: o.name, description: o.description, reshuffle: o.reshuffle !== undefined ? o.reshuffle : true };
//     // });
//   }
// }

export class SpellCountRules {
  max_count: UpgradableNumber = new UpgradableNumber();
  slot_based_count = false;

  constructor(obj?: any) {
    if (obj) {
      this.max_count = obj.max_count ? new UpgradableNumber(obj.max_count) : new UpgradableNumber();
      this.slot_based_count = obj.slot_based_count ? obj.slot_based_count : false;
    }
  }

  toDBObj(): any {
    return {
      max_count: this.max_count.toDBObj(),
      slot_based_count: this.slot_based_count
    };
  }

  clone(): SpellCountRules {
    const new_obj = new SpellCountRules();
    new_obj.copy(this);
    return new_obj;
  }

  copy(copyMe: SpellCountRules): void {
    this.max_count = copyMe.max_count.clone();
    this.slot_based_count = copyMe.slot_based_count;
  }
}